<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import NucleoWorkOrderConfirmationDialog from './NucleoWorkOrderConfirmationDialog.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  action: { type: String, default: '' },
  service: { type: Object, default: null },
  saving: { type: Boolean, default: false },
});
const emit = defineEmits(['close', 'save']);
const { t } = useI18n();
const form = ref({ scheduled_for: '', observacao: '', files: [] });
const dialogCopy = computed(() => ({
  schedule: {
    title: t('NUCLEO_WORK_ORDERS.DETAIL.DIALOG.TITLE.SCHEDULE'),
    copy: t('NUCLEO_WORK_ORDERS.DETAIL.DIALOG.COPY.SCHEDULE'),
    action: t('NUCLEO_WORK_ORDERS.DETAIL.ACTIONS.SCHEDULE'),
  },
  start: {
    title: t('NUCLEO_WORK_ORDERS.DETAIL.DIALOG.TITLE.START'),
    copy: t('NUCLEO_WORK_ORDERS.DETAIL.DIALOG.COPY.START'),
    action: t('NUCLEO_WORK_ORDERS.DETAIL.ACTIONS.START'),
  },
  record_evidence: {
    title: t('NUCLEO_WORK_ORDERS.DETAIL.DIALOG.TITLE.RECORD_EVIDENCE'),
    copy: t('NUCLEO_WORK_ORDERS.DETAIL.DIALOG.COPY.RECORD_EVIDENCE'),
    action: t('NUCLEO_WORK_ORDERS.DETAIL.ACTIONS.RECORD_EVIDENCE'),
  },
  complete: {
    title: t('NUCLEO_WORK_ORDERS.DETAIL.DIALOG.TITLE.COMPLETE'),
    copy: t('NUCLEO_WORK_ORDERS.DETAIL.DIALOG.COPY.COMPLETE'),
    action: t('NUCLEO_WORK_ORDERS.DETAIL.ACTIONS.COMPLETE'),
  },
  accept: {
    title: t('NUCLEO_WORK_ORDERS.DETAIL.DIALOG.TITLE.ACCEPT'),
    copy: t('NUCLEO_WORK_ORDERS.DETAIL.DIALOG.COPY.ACCEPT'),
    action: t('NUCLEO_WORK_ORDERS.DETAIL.ACTIONS.ACCEPT'),
  },
}));
const currentCopy = computed(() => dialogCopy.value[props.action] || {});
const requiresSchedule = computed(() => props.action === 'schedule');
const requiresEvidence = computed(() => props.action === 'record_evidence');
const usesReferenceConfirmation = computed(() => props.action === 'accept');
const valid = computed(() => {
  if (requiresSchedule.value) return Boolean(form.value.scheduled_for);
  return (
    !requiresEvidence.value ||
    Boolean(form.value.observacao || form.value.files.length)
  );
});

watch(
  () => props.show,
  visible => {
    if (visible) form.value = { scheduled_for: '', observacao: '', files: [] };
  }
);

const selectFiles = event => {
  form.value.files = Array.from(event.target.files || []);
};
const save = () =>
  emit('save', {
    action: props.action,
    serviceId: props.service?.id,
    ...form.value,
  });
</script>

<template>
  <NucleoWorkOrderConfirmationDialog
    v-if="usesReferenceConfirmation"
    :show="show"
    :saving="saving"
    variant="finalize"
    @close="emit('close')"
    @confirm="save"
  />
  <LaisModal
    v-else
    :show="show"
    :title="currentCopy.title"
    max-width-class="max-w-[560px]"
    @close="emit('close')"
  >
    <div class="grid gap-4">
      <p class="text-sm text-lais-text-body">{{ currentCopy.copy }}</p>
      <LaisField
        v-if="requiresSchedule"
        :label="t('NUCLEO_WORK_ORDERS.DETAIL.FIELDS.SCHEDULED_FOR')"
        required
      >
        <input
          v-model="form.scheduled_for"
          type="datetime-local"
          class="min-h-10 rounded-lais border border-lais-border bg-white px-3 text-sm"
        />
      </LaisField>
      <LaisField :label="t('NUCLEO_WORK_ORDERS.DETAIL.FIELDS.OBSERVATION')">
        <textarea
          v-model="form.observacao"
          rows="4"
          class="rounded-lais border border-lais-border bg-white px-3 py-2 text-sm"
        />
      </LaisField>
      <LaisField
        v-if="requiresEvidence"
        :label="t('NUCLEO_WORK_ORDERS.DETAIL.FIELDS.EVIDENCE')"
        :helper="t('NUCLEO_WORK_ORDERS.DETAIL.FIELDS.EVIDENCE_HELPER')"
      >
        <input
          type="file"
          multiple
          class="text-sm text-lais-text-body"
          @change="selectFiles"
        />
      </LaisField>
    </div>
    <template #footer>
      <div class="flex justify-end gap-3">
        <LaisPrimaryButton variant="ghost" @click="emit('close')">
          {{ t('NUCLEO_WORK_ORDERS.DETAIL.ACTIONS.CANCEL') }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          :disabled="!valid"
          :is-loading="saving"
          @click="save"
        >
          {{ currentCopy.action }}
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>
</template>
